import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getMeals } from "@/packages/dashboard/api/get-meals";
import { getDayBounds } from "@/packages/dashboard/utils/date-range-helpers";
import {
  addTotals,
  buildMealTotals,
  computeItemTotals,
  createEmptyTotals,
  type MacroTotals,
} from "@/packages/dashboard/Home/Meals/macro-aggregations";

import type { MealEntry } from "./use-meal-form";

const computeMealEntryTotals = (entries: MealEntry[]): MacroTotals => {
  const totals = createEmptyTotals();

  entries.forEach((entry) => {
    if (!entry.foodItem) {
      return;
    }

    addTotals(totals, computeItemTotals(entry.quantity, entry.foodItem));
  });

  return totals;
};

type UseDailyTargetProgressParams = {
  mealTimestamp: Date | null;
  currentMealId: number | null;
  mealEntries: MealEntry[];
};

export const useDailyTargetProgress = ({
  mealTimestamp,
  currentMealId,
  mealEntries,
}: UseDailyTargetProgressParams) => {
  const dayBounds = useMemo(() => (mealTimestamp ? getDayBounds(mealTimestamp) : null), [mealTimestamp]);

  const {
    data: mealsResponse,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["daily-meals", dayBounds?.key],
    queryFn: () => {
      if (!dayBounds) {
        throw new Error("Missing day bounds for daily meals request");
      }

      return getMeals({
        start_timestamp: dayBounds.start_timestamp,
        end_timestamp: dayBounds.end_timestamp,
      });
    },
    enabled: Boolean(dayBounds),
    staleTime: 60_000,
  });

  const baselineTotals = useMemo(() => {
    const totals = createEmptyTotals();
    if (!mealsResponse?.meals) {
      return totals;
    }

    mealsResponse.meals.forEach((meal) => {
      if (currentMealId && meal.id === currentMealId) {
        return;
      }

      addTotals(totals, buildMealTotals(meal));
    });

    return totals;
  }, [currentMealId, mealsResponse?.meals]);

  const pendingTotals = useMemo(() => computeMealEntryTotals(mealEntries), [mealEntries]);

  const projectedTotals = useMemo(() => {
    const totals = createEmptyTotals();
    addTotals(totals, baselineTotals);
    addTotals(totals, pendingTotals);
    return totals;
  }, [baselineTotals, pendingTotals]);

  return {
    dayLabel: dayBounds?.label ?? null,
    baselineTotals,
    pendingTotals,
    projectedTotals,
    isDaySelected: Boolean(dayBounds),
    isLoading,
    isFetching,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
  };
};
